import type { TFunction } from "react-i18next"

import { MilestoneDependencyType } from "../../types/project-milestone-dependency.types.ts"

// Which edge of the source/target milestone a relationship type reads from —
// shared by the Gantt's connector geometry and the date-propagation engine so
// both agree on what "Finish → Start" etc. actually anchors on.
const sourceSideFor = (type: MilestoneDependencyType): "start" | "end" =>
  type === MilestoneDependencyType.START_TO_START ||
  type === MilestoneDependencyType.START_TO_FINISH
    ? "start"
    : "end"

const targetSideFor = (type: MilestoneDependencyType): "start" | "end" =>
  type === MilestoneDependencyType.FINISH_TO_FINISH ||
  type === MilestoneDependencyType.START_TO_FINISH
    ? "end"
    : "start"

// Finish→Start is the only relationship with an implicit one-day gap: a
// milestone's end date is its last active day, so the next one starts the
// day after by default (lag adds on top of that). The other three types can
// legitimately land on the same day (e.g. two milestones starting together),
// so they carry no implicit gap.
const impliedGapDaysFor = (type: MilestoneDependencyType): number =>
  type === MilestoneDependencyType.FINISH_TO_START ? 1 : 0

// An overlap may not reorder the two milestones, so a start-anchored target
// reaches back at most to its source's start. `undefined` where nothing bounds
// it: an undated source, or the end-anchored types the engine leaves unfloored.
const minLagDaysFor = (
  type: MilestoneDependencyType,
  sourceDurationDays: number | null
): number | undefined =>
  targetSideFor(type) !== "start" || sourceDurationDays === null
    ? undefined
    : -(
        (sourceSideFor(type) === "end" ? sourceDurationDays : 0) +
        impliedGapDaysFor(type)
      )

// A negative lag is an overlap, not a "-2 day lag" — one decision, shared by
// every surface that prints one.
const lagPhrase = (t: TFunction, days: number): string | null =>
  days
    ? t(`milestoneDependencies.${days < 0 ? "overlap" : "lag"}`, {
        count: Math.abs(days)
      })
    : null

// The compact form, for the Gantt's bars.
const lagPhraseShort = (t: TFunction, days: number): string | null =>
  days
    ? t(`milestoneDependencies.${days < 0 ? "overlapShort" : "lagShort"}`, {
        count: Math.abs(days)
      })
    : null

const TYPE_INFO: Record<
  MilestoneDependencyType,
  { code: string; label: string }
> = {
  [MilestoneDependencyType.FINISH_TO_START]: {
    code: "FS",
    label: "Finish → Start"
  },
  [MilestoneDependencyType.START_TO_START]: {
    code: "SS",
    label: "Start → Start"
  },
  [MilestoneDependencyType.FINISH_TO_FINISH]: {
    code: "FF",
    label: "Finish → Finish"
  },
  [MilestoneDependencyType.START_TO_FINISH]: {
    code: "SF",
    label: "Start → Finish"
  }
}

export {
  sourceSideFor,
  targetSideFor,
  impliedGapDaysFor,
  minLagDaysFor,
  lagPhrase,
  lagPhraseShort,
  TYPE_INFO
}
